import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import EmailUser from "@/domain/entities/emails/EmailUser";
import UserProfile from "@/domain/entities/user/UserProfile";
import useGetUserAvatar from "@/hooks/useGetUserAvatar";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import UserInfoCard from "./UserInfoCard";

type SenderAvatarProps = {
    user?: EmailUser;
    className?: string;
    fallbackClassName?: string;
    text?: string;
    showTooltip?: boolean;
    currentUserProfile?: UserProfile;
};

const SenderAvatar = ({
    user,
    className,
    fallbackClassName,
    text,
    currentUserProfile,
    showTooltip = true,
}: SenderAvatarProps) => {
    if (!user && !text) {
        return null;
    }

    const { data: avatarUrl } = useGetUserAvatar(user?.email ?? "");

    if (!user && text) {
        return (
            <Avatar
                className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full",
                    className,
                )}
            >
                <AvatarFallback
                    className={cn(
                        "flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 bg-gray-200 text-[10px] font-normal text-gray-600",
                        fallbackClassName,
                    )}
                >
                    {text}
                </AvatarFallback>
            </Avatar>
        );
    }

    const usedAvatar =
        currentUserProfile?.profile.email === user!.email
            ? currentUserProfile!.photoURL
            : avatarUrl;

    const avatar = (
        <Avatar
            className={cn(
                "flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full",
                className,
            )}
        >
            {usedAvatar && (
                <AvatarImage
                    alt="Profile"
                    onError={(e) => {
                        console.error("Avatar image failed to load:", e);
                        e.currentTarget.style.display = "none";
                    }}
                    src={usedAvatar ?? undefined}
                />
            )}
            <AvatarFallback
                className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 bg-gray-200 text-[8px] text-gray-700",
                    fallbackClassName,
                )}
            >
                {text ?? user!.getInitials}
            </AvatarFallback>
        </Avatar>
    );

    if (!showTooltip) {
        return avatar;
    }

    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>{avatar}</TooltipTrigger>
                <TooltipContent
                    align="center"
                    className="bg-transparent p-0"
                    side="bottom"
                >
                    <UserInfoCard avatarUrl={avatarUrl} user={user!} />
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default SenderAvatar;
